import { Button } from 'app/shared/components/generic-ui/Button/Button';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { translations } from 'app/locales/i18n';
import { useTranslation } from 'react-i18next';
import { useStyles } from './TicketFilterBlock.styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  InputSize,
  TextInput,
} from 'app/shared/components/generic-ui/TextInput/TextInput';
import { useNavigate } from 'react-router';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import { useBatchHistoryList } from 'app/shared/hooks';
import { IHistoryRecord, IMetaData, Routes } from 'types';

interface ITicketFilterBlockProps {
  withStatusesOnly: boolean;
  loadingHandler: (value: boolean) => void;
  ticketsHistoryHandler: (
    items?: IHistoryRecord[],
    metadata?: IMetaData,
  ) => void;
  refresh: boolean;
  toJobRemaining: boolean;
  predictions: boolean;
  autoRefresh: boolean;
}

export const TicketFilterBlock = ({
  withStatusesOnly,
  ticketsHistoryHandler,
  loadingHandler,
  refresh,
  toJobRemaining,
  predictions,
  autoRefresh,
}: ITicketFilterBlockProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { organizationId }: { organizationId: string } = useRouteParams();
  const navigate = useNavigate();
  const ticketId = new URLSearchParams(location.search).get('ticketId');

  const [currentTicketId, setCurrentTicketId] = useState(
    localStorage.getItem('ticketId') || '',
  );

  const ticketsHistoryQueryParams = useMemo(() => {
    return {
      organizationId: organizationId,
      queryParameters: {
        ticketsWithStatusesOnly: `${withStatusesOnly}`,
        ticketNumber: ticketId || '',
        ToJobRemaining: toJobRemaining,
        Predictions: predictions,
      },
    };
  }, [organizationId, withStatusesOnly, ticketId, toJobRemaining, predictions]);

  const {
    fetchTicketHistory,
    isFetchingTicketHistory,
    batchesHistoryMetadata,
    batchesHistory,
  } = useBatchHistoryList(ticketsHistoryQueryParams);

  const applyFilterHandler = useCallback(async () => {
    localStorage.setItem('batchesFrom', '');
    localStorage.setItem('batchesTo', '');
    localStorage.setItem('batchesPlants', '[]');
    localStorage.setItem('batchesCustomers', '[]');
    localStorage.setItem('batchesMixDesigns', '[]');
    localStorage.setItem('batchesVehicles', '[]');
    localStorage.setItem('batchesDrivers', '[]');
    localStorage.setItem('batchesProjects', '[]');
    localStorage.setItem('batchesPourEvents', '[]');
    localStorage.setItem('selectedUsers', '[]');
    localStorage.setItem('batchesEventStatusCodes', '[]');
    localStorage.setItem('ticketProducts', '[]');
    localStorage.setItem('crossShippingGroups', '[]');

    localStorage.setItem('ticketId', currentTicketId);

    const searchParams = new URLSearchParams({
      ['ticketId']: currentTicketId,
    });

    navigate({
      pathname: Routes.Batches.replace(
        ':organizationId/:page/:perPage',
        `${organizationId}/1/20`,
      ),
      search: `${searchParams}`,
    });
  }, [currentTicketId, navigate, organizationId]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (ticketId) {
      fetchTicketHistory();
      if (autoRefresh) {
        interval = setInterval(() => {
          fetchTicketHistory();
        }, 30000);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    fetchTicketHistory,
    ticketId,
    toJobRemaining,
    predictions,
    refresh,
    autoRefresh,
  ]);

  useEffect(() => {
    if (ticketId) {
      ticketsHistoryHandler(batchesHistory, batchesHistoryMetadata);
    }
  }, [batchesHistory, batchesHistoryMetadata, ticketId, ticketsHistoryHandler]);

  useEffect(() => {
    loadingHandler(isFetchingTicketHistory);
  }, [isFetchingTicketHistory, loadingHandler]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.input}>
        <CustomTypography variant="caption1" color="greyscale1">
          {t(translations.filterBlock.ticket)}
        </CustomTypography>

        <TextInput
          name="ticketId"
          onKeyPress={e => {
            if (!new RegExp('^[0-9]$').test(e.key)) {
              e.preventDefault();
            }
          }}
          borderRadius={2}
          customSize={InputSize.BIG}
          currentValue={currentTicketId}
          placeholder={`${t(translations.filterBlock.typeHere)}...`}
          onChangeHandler={e => {
            setCurrentTicketId(e?.target.value || '');
          }}
        />
      </div>

      <div className={classes.applyButton}>
        <Button
          onClick={applyFilterHandler}
          height={42}
          variant={'secondaryLight'}
        >
          <CustomTypography variant="buttonTextSmall" bold color={'mbsBlue'}>
            {t(translations.datePicker.apply)}
          </CustomTypography>
        </Button>
      </div>
    </div>
  );
};
